import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, Phone, Mail, MapPin, CheckCircle, AlertCircle } from 'lucide-react';
import { companyInfo } from '../data/mock';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      if (response.data.status === 'success') {
        setIsSubmitted(true);
        toast.success('Message sent successfully! We will get back to you soon.');
        
        // Reset form after 5 seconds
        setTimeout(() => {
          setIsSubmitted(false);
          setFormData({
            name: '',
            email: '',
            phone: '',
            service: '',
            message: '',
          });
        }, 5000);
      }
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error('Failed to send message. Please try again or call us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 lg:py-32 bg-black">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16">
          {/* Left Side - Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="service-button bg-cyan-500/20 text-cyan-400 border-cyan-500/30 mb-4 inline-block">
              Contact Us
            </span>
            <h2 className="section-heading text-white mt-4 mb-6">
              Let&apos;s Build Something
              <span className="text-cyan-400"> Amazing</span>
            </h2>
            <p className="body-large text-white/60 mb-10 max-w-lg">
              Ready to transform your digital presence? Get in touch and let&apos;s discuss how we can help your business grow.
            </p>

            {/* Promo Banner */}
            <div className="bg-yellow-500/20 border border-yellow-500/30 rounded-xl p-6 mb-10">
              <div className="flex items-center gap-3 mb-2">
                <Phone size={24} className="text-yellow-400" />
                <span className="text-yellow-400 font-bold text-lg">Special Offer!</span>
              </div>
              <p className="text-white/80 mb-3">
                Call us today and receive <span className="text-yellow-400 font-bold">20% OFF</span> on your first service!
              </p>
              <a
                href={`tel:${companyInfo.phone}`}
                className="inline-flex items-center gap-2 bg-yellow-500 text-black px-6 py-3 rounded-full font-bold hover:bg-yellow-400 transition-colors"
              >
                <Phone size={18} />
                {companyInfo.phone}
              </a>
            </div>

            {/* Contact Details */}
            <div className="space-y-6">
              <a
                href={`tel:${companyInfo.phone}`}
                className="flex items-center gap-4 text-white/80 hover:text-cyan-400 transition-colors group"
              >
                <div className="w-12 h-12 rounded-lg bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center group-hover:bg-cyan-500/20 transition-colors">
                  <Phone size={20} className="text-cyan-400" />
                </div>
                <div>
                  <span className="block text-sm text-white/50 font-mono">PHONE</span>
                  <span className="body-large">{companyInfo.phone}</span>
                </div>
              </a>

              <a
                href={`mailto:${companyInfo.email}`}
                className="flex items-center gap-4 text-white/80 hover:text-cyan-400 transition-colors group"
              >
                <div className="w-12 h-12 rounded-lg bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center group-hover:bg-cyan-500/20 transition-colors">
                  <Mail size={20} className="text-cyan-400" />
                </div>
                <div>
                  <span className="block text-sm text-white/50 font-mono">EMAIL</span>
                  <span className="body-large">{companyInfo.email}</span>
                </div>
              </a>

              <div className="flex items-center gap-4 text-white/80">
                <div className="w-12 h-12 rounded-lg bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center">
                  <MapPin size={20} className="text-cyan-400" />
                </div>
                <div>
                  <span className="block text-sm text-white/50 font-mono">LOCATION</span>
                  <span className="body-large">{companyInfo.location}</span>
                </div>
              </div>
            </div>

            {/* Brand Image */}
            <div className="hidden lg:block mt-10">
              <img
                src={companyInfo.logoDark}
                alt={companyInfo.name}
                className="h-20 w-auto opacity-50"
              />
            </div>
          </motion.div>

          {/* Right Side - Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="bg-white/5 border border-white/10 rounded-xl p-8">
              {isSubmitted ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', stiffness: 200, damping: 10 }}
                    className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mb-6"
                  >
                    <CheckCircle size={40} className="text-green-400" />
                  </motion.div>
                  <h3 className="card-heading text-white mb-2">Message Sent!</h3>
                  <p className="text-white/60 mb-4">We&apos;ll get back to you within 24 hours.</p>
                  <p className="text-cyan-400 text-sm">
                    Or call us now at <a href={`tel:${companyInfo.phone}`} className="underline font-bold">{companyInfo.phone}</a> for immediate assistance!
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm text-white/50 font-mono mb-2">NAME *</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-cyan-500/50 transition-colors"
                        placeholder="John Doe"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-white/50 font-mono mb-2">EMAIL *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-cyan-500/50 transition-colors"
                        placeholder="john@example.com"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm text-white/50 font-mono mb-2">PHONE</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-cyan-500/50 transition-colors"
                        placeholder="(555) 123-4567"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-white/50 font-mono mb-2">SERVICE</label>
                      <select
                        name="service"
                        value={formData.service}
                        onChange={handleChange}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-cyan-500/50 transition-colors"
                      >
                        <option value="" className="bg-black">Select a service</option>
                        <option value="website" className="bg-black">Website Development</option>
                        <option value="social" className="bg-black">Social Media Marketing</option>
                        <option value="digital" className="bg-black">Digital Marketing (PPC/SEO)</option>
                        <option value="branding" className="bg-black">Branding & Creative</option>
                        <option value="other" className="bg-black">Other</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm text-white/50 font-mono mb-2">MESSAGE *</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-cyan-500/50 transition-colors resize-none"
                      placeholder="Tell us about your project..."
                    />
                  </div>

                  <motion.button
                    type="submit"
                    disabled={isSubmitting}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="cta-button w-full bg-cyan-500 text-black border-cyan-500 hover:bg-cyan-400 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <span>Sending...</span>
                    ) : (
                      <>
                        <span>Send Message</span>
                        <Send size={16} />
                      </>
                    )}
                  </motion.button>

                  <p className="text-center text-white/40 text-sm">
                    We typically respond within 24 hours. For immediate assistance, call us!
                  </p>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
